import { useEffect, useRef, useState, useCallback } from "react";
import { Routes, Route } from "react-router-dom";
import FilterControls from "./components/FilterControls";
import Plots from "./components/Plots";
import DwhPlots from "./components/DwhPlots";
import CustomPlots from "./components/CustomPlots";
import SearchPopup from "./components/SearchPopup";
import SideNav from "./components/SideNav";
import GroupFilter from "./components/GroupFilter";
import AudienceListPage from "./pages/AudienceListPage";
import BudgetListPage from "./pages/BudgetListPage";
import BudgetDetailPage from "./pages/BudgetDetailPage";
import AudienceDetailPage from "./pages/AudienceDetailPage";
import { useData } from "./data/DataContext";
import { useOutlierPlots } from "./data/useOutlierPlots";

function App() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [dwhExpanded, setDwhExpanded] = useState(true);
  const [outlierOnly, setOutlierOnly] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerEl, setHeaderEl] = useState<HTMLDivElement | null>(null);
  const lastScrollY = useRef(0);
  const lastDocHeight = useRef(0);

  const { preparedData } = useData();
  const outlierPlotIds = useOutlierPlots(preparedData ?? null);

  // Measure header height so the content spacer stays in sync.
  // Uses state-based ref so the observer re-attaches when navigating back to "/".
  useEffect(() => {
    if (!headerEl) return;
    const observer = new ResizeObserver(() => {
      setHeaderHeight(headerEl.offsetHeight);
    });
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, [headerEl]);

  const headerRefCallback = useCallback((el: HTMLDivElement | null) => {
    setHeaderEl(el);
    // Measure immediately on mount to avoid a 0→measured paddingTop jump
    if (el) setHeaderHeight(el.offsetHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const heightChanged = Math.abs(docHeight - lastDocHeight.current) > 5;
      lastDocHeight.current = docHeight;

      // Ignore scroll events caused by content reflow (document height changed)
      if (heightChanged) {
        lastScrollY.current = currentY;
        return;
      }

      if (currentY < 80 || currentY < lastScrollY.current - 5) {
        setHeaderVisible(true);
      } else if (currentY > lastScrollY.current + 5) {
        setHeaderVisible(false);
      }
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const navClass = isNavOpen ? "main-content--nav-open" : "";

  return (
    <>
      <SideNav
        isOpen={isNavOpen}
        onToggle={() => setIsNavOpen(prev => !prev)}
        selectedTag={selectedTag}
        selectedOwner={selectedOwner}
      />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <div
                ref={headerRefCallback}
                className={`sticky-header ${isNavOpen ? "sticky-header--nav-open" : ""} ${headerVisible ? "" : "sticky-header--hidden"}`}
              >
                <FilterControls />
                <GroupFilter
                  selectedTag={selectedTag}
                  onTagChange={setSelectedTag}
                  selectedOwner={selectedOwner}
                  onOwnerChange={setSelectedOwner}
                  outlierPlotIds={outlierPlotIds}
                  outlierOnly={outlierOnly}
                  onOutlierToggle={() => setOutlierOnly(prev => !prev)}
                />
              </div>
              <div className={`main-content ${navClass}`} style={{ paddingTop: headerHeight }}>
                <h2 className="section-header">
                  DWH Metrics
                  <button
                    type="button"
                    className="section-header__toggle"
                    onClick={() => setDwhExpanded(prev => !prev)}
                    aria-expanded={dwhExpanded}
                  >
                    {dwhExpanded ? "Hide" : "Show"}
                  </button>
                </h2>
                {dwhExpanded && (
                  <DwhPlots selectedTag={selectedTag} selectedOwner={selectedOwner} outlierOnly={outlierOnly} />
                )}
                <h2 className="section-header">WPR Metrics</h2>
                <Plots
                  selectedTag={selectedTag}
                  selectedOwner={selectedOwner}
                  outlierOnly={outlierOnly}
                  outlierPlotIds={outlierPlotIds}
                />
                <h2 className="section-header">Custom Charts</h2>
                <CustomPlots />
              </div>
              <SearchPopup
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
                selectedTag={selectedTag}
                selectedOwner={selectedOwner}
                outlierOnly={outlierOnly}
                outlierPlotIds={outlierPlotIds}
              />
            </>
          }
        />
        <Route
          path="/audiences"
          element={
            <div className={`main-content ${navClass}`}>
              <AudienceListPage />
            </div>
          }
        />
        <Route
          path="/audiences/:id"
          element={
            <div className={`main-content ${navClass}`}>
              <AudienceDetailPage />
            </div>
          }
        />
        <Route
          path="/budgets"
          element={
            <div className={`main-content ${navClass}`}>
              <BudgetListPage />
            </div>
          }
        />
        <Route
          path="/budgets/:id"
          element={
            <div className={`main-content ${navClass}`}>
              <BudgetDetailPage />
            </div>
          }
        />
      </Routes>
    </>
  );
}

export default App;
